import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Suspense, useMemo } from "react";
import { useFragment } from "react-relay";

import { Avatar } from "@whoosh/design-system/src/components/display";
import {
  Box,
  HStack,
  VStack,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import { AddPeopleSearch_Atom } from "../../../../../AddPatrons/atoms/stores";
import { AddPeopleSearchbox } from "../../../../../AddPatrons/components/AddPeopleSearchBox";
import { useDebouncedSearch } from "../../../../../AddPatrons/hooks/useDebouncedSearch";
import {
  AddPlayers,
  AddPlayersShareDisclaimer,
  Player,
  PlayersListSkeleton,
} from "../../../../../AddPlayers";
import { addPlayerAtom } from "../../../../atoms/actions/addPlayer/addPlayerAtom";
import { removePlayerAtom } from "../../../../atoms/actions/removePlayer/removePlayerAtom";
import { PlayersSelectorAtom } from "../../../../atoms/selectors/playersSelectorAtom/playersSelectorAtom";
import {
  BookingWizardDataAtom,
  CurrentRequestGroupIndexAtom,
} from "../../../../atoms/stores";
import { BookingWizard } from "../../../../types";
import { AddPlayersStepClientProfile$key } from "./__generated__/AddPlayersStepClientProfile.graphql";
import { AddPlayersStepNode$key } from "./__generated__/AddPlayersStepNode.graphql";
import { useSetValidTransportationType } from "./hooks/useSetValidTransportationType";
import { getExcludedPlayerIds } from "./utils/getExcludedPlayerIds";
import { getOtherPlayerCapacity } from "./utils/getOtherPlayerCapacity";
import { isBookingFull as checkIsBookingFull } from "./utils/isBookingFull";

type Props = {
  clientProfileRef: AddPlayersStepClientProfile$key | null;
  nodeRef: AddPlayersStepNode$key | null;
};

export const AddPlayersStep = ({ clientProfileRef, nodeRef }: Props) => {
  const data = useFragment(AddPlayersStepFragmentString, clientProfileRef);
  const node = useFragment(AddPlayersStepNodeFragmentString, nodeRef);
  const [handleSetValidTransportationType] = useSetValidTransportationType();

  const club = data?.member?.club;

  const bookingWizardData = useAtomValue(BookingWizardDataAtom);
  const currentRequestGroupIndex = useAtomValue(CurrentRequestGroupIndexAtom);

  const groupSize = bookingWizardData?.groupSize || 1;

  const players = useAtomValue(PlayersSelectorAtom);

  const [search, setSearch] = useAtom(AddPeopleSearch_Atom);
  const debouncedSearch = useDebouncedSearch(search);

  const otherPlayerCapacity = getOtherPlayerCapacity({
    groupSize,
    playersLength: players.length,
  });

  const addPlayerToBooking = useSetAtom(addPlayerAtom);

  const removePlayerFromBooking = useSetAtom(removePlayerAtom);

  const isBookingFull = checkIsBookingFull({
    playersLength: players.length,
    groupSize,
    bookingType: bookingWizardData?.type,
    bookingWizardCapacity:
      bookingWizardData?.type === "RequestBooking"
        ? bookingWizardData?.capacity ?? 0
        : undefined,
  });

  const selectedPlayerIds = useMemo(
    () => players.flatMap((player) => player.id ?? []),
    [players]
  );
  const excludedPlayerIds = useMemo(
    () =>
      getExcludedPlayerIds({
        rounds: bookingWizardData?.rounds ?? [],
        isRequestBooking: bookingWizardData?.type === "RequestBooking",
        currentRequestGroupIndex,
      }),
    [
      bookingWizardData?.rounds,
      bookingWizardData?.type,
      currentRequestGroupIndex,
    ]
  );

  const onTogglePlayer = (isChecked: boolean, player: Player) => {
    if (isChecked && player.clientType !== "unknownCustomer") {
      const newPlayer: BookingWizard.RoundPlayer = {
        id: player.id ?? "",
        firstName: player.firstName ?? null,
        lastName: player.lastName ?? null,
        clientType: player.clientType,
        profileImagePublicId: player.profileImagePublicId ?? null,
        membershipTypeId: player.membershipTypeId ?? "",
      };
      addPlayerToBooking(newPlayer);

      if (bookingWizardData?.type === "GolfBooking") {
        const hasNewMembershipType = bookingWizardData?.rounds.some((round) => {
          if (round.player.clientType === "customer") {
            return round.player.membershipTypeId !== player.membershipTypeId;
          }
          return false;
        });

        if (hasNewMembershipType) {
          handleSetValidTransportationType(
            bookingWizardData?.startSlotId ?? "",
            player.id ?? "",
            bookingWizardData?.transportationTypeId ?? ""
          );
        }
      }
    } else {
      removePlayerFromBooking(player.id ?? "");
    }
  };

  // Determine if we should show a condensed view of available players
  const showCondensedView = groupSize > 6 && node?.requirePlayerCounts;

  return (
    <Box flex={1} bg="surface-secondary">
      <HStack
        px={5}
        py={4}
        alignItems="center"
        justifyContent="flex-start"
        gap={2}
        borderBottomWidth={1}
        borderColor="outline-default"
        bg="surface-primary"
        flexWrap="wrap"
      >
        {showCondensedView ? (
          <HStack alignItems="center" gap={2}>
            <Avatar size={"sm"} type={"unknownCustomer"} />
            <Text variant="mainBaseSemiBold">
              {otherPlayerCapacity}{" "}
              {otherPlayerCapacity === 1
                ? t`Available spot`
                : t`Available spots`}
            </Text>
          </HStack>
        ) : (
          <>
            {players.map((player) => (
              <Avatar
                key={player.id}
                profileImagePublicId={
                  player.clientType !== "unknownCustomer" &&
                  player.clientType !== "unknownGuest"
                    ? player.profileImagePublicId ?? undefined
                    : undefined
                }
                firstName={player.firstName ?? null}
                lastName={player.lastName ?? null}
                size={"sm"}
                type={player.clientType}
              />
            ))}
            {Array.from({ length: otherPlayerCapacity }).map((_, index) => (
              <Avatar key={index} size={"sm"} type={"customer"} />
            ))}
          </>
        )}
      </HStack>

      <VStack mt={2} px={5} py={4} bg="surface-primary" gap={5}>
        <AddPeopleSearchbox search={search} setSearch={setSearch} px={0} />
        <Suspense fallback={<PlayersListSkeleton />}>
          <AddPlayers
            clubRef={club ?? null}
            selectedPlayerIds={selectedPlayerIds}
            excludedPlayerIds={excludedPlayerIds}
            isBookingFull={isBookingFull}
            search={debouncedSearch}
            onTogglePlayer={(isChecked, player) =>
              onTogglePlayer(isChecked, player)
            }
          />
        </Suspense>
        <AddPlayersShareDisclaimer />
      </VStack>
    </Box>
  );
};

export const AddPlayersStepNodeFragmentString = graphql`
  fragment AddPlayersStepNode on Node {
    ... on QueueSlot {
      __typename
    }
    ... on TimeSlot {
      requirePlayerCounts
    }
  }
`;

export const AddPlayersStepFragmentString = graphql`
  fragment AddPlayersStepClientProfile on ClientProfile
  @argumentDefinitions(players: { type: "[ID]" }) {
    member {
      club {
        ...AddPlayers_Clients @arguments(players: $players)
      }
    }
  }
`;
